import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuthPrompt } from "../../features/auth/authHooks";
import {
  clearErrors,
  loginAsync,
  setLoginModalState,
  setPasswordAsync,
  sendOTPAsync,
  signinWithOTPAsync
} from "../../features/auth/authSlice";
import { clearErrors as clearUserErrors, upsertUserAsync } from "../../features/user/userSlice";

import axios from "axios";
import { flashDetailedToastNotification } from "../../features/toast-notification/toastNotificationSlice";
import LoadingSpinner from "../LoadingSpinner";

function InputField({
  value,
  onChange,
  name,
  label,
  type,
  required,
  autoFocus,
  tabIndex,
  placeholder
}) {
  return (
    <div className="mt-4">
      <label htmlFor={name} className="block text-sm font-medium leading-5 text-gray-900">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type || "text"}
        required={required}
        autoFocus={autoFocus}
        tabIndex={tabIndex}
        placeholder={placeholder}
        className="mt-1 shadow-sm appearance-none block w-full px-3 py-3 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

function OTPInput({ value, onChange, autoFocus }) {
  const inputRefs = React.useRef([]);
  const [digits, setDigits] = React.useState(['', '', '', '', '', '']);

  // Update digits when value prop changes
  React.useEffect(() => {
    const valueStr = (value || '').toString();
    const newDigits = valueStr.split('').slice(0, 6);
    while (newDigits.length < 6) newDigits.push('');
    setDigits(newDigits);
  }, [value]);

  const handleChange = (index, digitValue) => {
    // Only allow single digit
    const newValue = digitValue.slice(-1);
    if (newValue && !/^\d$/.test(newValue)) return;

    const newDigits = [...digits];
    newDigits[index] = newValue;
    setDigits(newDigits);

    // Update parent form value
    const otpValue = newDigits.join('');
    onChange({ target: { name: 'otp', value: otpValue } });

    // Move to next input if digit was entered
    if (newValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newDigits = pastedData.split('');
    while (newDigits.length < 6) newDigits.push('');
    setDigits(newDigits);

    // Update parent form value
    onChange({ target: { name: 'otp', value: pastedData } });

    // Focus last filled input or first empty
    const focusIndex = Math.min(pastedData.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className="mt-4">
      <div className="flex gap-2 justify-center">
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={el => inputRefs.current[index] = el}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            autoFocus={autoFocus && index === 0}
            tabIndex={index + 1}
            className="w-12 h-14 text-center text-2xl font-semibold border-2 border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
          />
        ))}
      </div>
    </div>
  );
}

function ResetPasswordButton() {
  const dispatch = useDispatch();
  const { platformData } = useSelector(state => state.frontend);

  const handleClick = () => {
    dispatch(
      setLoginModalState({
        loginModalShowing: true,
        activeAuthForm: "resetPassword"
      })
    );
  };

  return (
    <div className="mt-2 flex items-center justify-center">
      <button
        className="font-medium text-blue-600 text-sm leading-5 hover:text-blue-500 focus:outline-none focus:underline transition ease-in-out duration-150"
        style={{ color: platformData?.primaryColor }}
        onClick={handleClick}
        type="button"
      >
        Forgot your password?
      </button>
    </div>
  );
}

// Back Arrow Icon Component
function BackIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

// Google Icon Component
function GoogleIcon() {
  return (
    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

const AuthForm = ({ formValues, setFormValues, initialFlow = "initial" }) => {
  const dispatch = useDispatch();
  const { dismissAuthPrompt } = useAuthPrompt();

  const [user, setUser] = useState(null);
  const [authFlowChosen, setAuthFlowChosen] = useState(initialFlow); // can be 'initial', 'email', 'otp', 'password', 'signup', or 'setPassword'
  const [isOTPSent, setIsOTPSent] = useState(false); // OTP sent to user's email
  const [errorPassword, setErrorPassword] = useState();
  const [loading, setLoading] = useState(false);

  const { error } = useSelector(state => state.auth);
  const { platformData } = useSelector(state => state.frontend);
  const isGoogleAuthEnabled = process.env.REACT_APP_AUTH_WITH_GOOGLE === "true";
  const isLocalhost3000 =
    typeof window !== "undefined" &&
    window?.location?.hostname === "localhost" &&
    window?.location?.port === "3000";
  // Include current location so backend can return user to the same page after OAuth
  const currentPath =
    typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "/";
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const redirectParam = encodeURIComponent(currentPath || "/");
  const originParam = currentOrigin ? `&origin=${encodeURIComponent(currentOrigin)}` : "";
  const googleBase = isLocalhost3000 ? "http://localhost:3001/api/auth/google" : "/api/auth/google";
  const platformParam = platformData?.id ? `&platformId=${platformData.id}` : "";
  const googleHref = `${googleBase}?redirect=${redirectParam}${platformParam}${originParam}`;

  // after the user enters their email there are multiple scenarios:
  const isInitialScreen = authFlowChosen === "initial";
  const isOTPLoginChosen = authFlowChosen === "otp";
  const isPasswordLogin = authFlowChosen === "password";
  const isPasswordUpdate = authFlowChosen === "setPassword";
  const isAccountCreation = authFlowChosen === "signup";

  const titleText = isAccountCreation
    ? `Welcome to ${platformData?.name || ""}!`
    : "Sign in to Access";
  const subText = isPasswordUpdate ? (
    "We're updating our security, please update your password below."
  ) : isAccountCreation ? (
    <>Let's create your account</>
  ) : isOTPLoginChosen && isOTPSent ? (
    "Check your email and enter your code to access"
  ) : null;

  // clear out password value if user enters set password flow
  useEffect(() => {
    if (isPasswordUpdate) setFormValues({ email: user.email });
  }, [isPasswordUpdate, setFormValues, user]);

  const onChoosePasswordLogin = () => {
    setAuthFlowChosen("password");
    // this is a bit of a shortcut, if the user first enters their email, then switches from OTP to password login
    // then check if they have a password set, if not, prompt them to set a password instead of login
    // this is done redundantly in the onSubmit function, but it's done here as well to make the UI more responsive
    if (formValues.email) {
      setLoading(true);
      // validate user by email to see if the user has a password set
      axios
        .get(`/auth/user-validation`, {
          params: { email: formValues.email, PlatformId: platformData.id }
        })
        .then(resp => {
          const user = resp.data;
          // if the user exists in the db but doesn't have a password set
          // we need to prompt them to update their password before logging in
          setUser(user);
          if (user && !user.isPasswordSet) setAuthFlowChosen("setPassword");
        })
        .catch(() => {
          // don't do anything here, we'll handle this in the onSubmit function
        })
        .finally(() => {
          setLoading(false);
          // set timeout of 0 to put this in the event loop so that it's executed after the form is rendered
          // state updates are batched, so if we don't do this, we'll be trying to focus the input before it's rendered
          setTimeout(() => document.querySelector("[name=email]")?.focus(), 0);
        });
    }
  };

  const onChange = event => {
    dispatch(clearErrors());
    setErrorPassword();

    const { name, value } = event.target;
    setFormValues(prevState => ({ ...prevState, [name]: value }));
  };

  const handleEmailSubmit = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`/auth/user-validation`, {
        params: { email: formValues.email, PlatformId: platformData.id }
      });
      setUser(data);

      // User exists - send OTP immediately
      await sendOTPAsync({ UserId: data.id });
      dispatch(
        flashDetailedToastNotification({ details: "One-Time Password sent to your email" })
      );
      setAuthFlowChosen("otp");
      setIsOTPSent(true);
    } catch (err) {
      if (err.response?.status === 404) {
        // User doesn't exist - go to signup
        setAuthFlowChosen("signup");
        setTimeout(() => document.querySelector("[name=name]")?.focus(), 0);
      } else {
        dispatch(
          flashDetailedToastNotification({ details: "Something went wrong", type: "error" })
        );
        console.error("Failed user validation", err);
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async event => {
    event.preventDefault();

    // Handle initial form submission with email
    if (isInitialScreen && formValues.email) {
      await handleEmailSubmit();
      return;
    }

    if (isOTPLoginChosen && !isOTPSent) {
      try {
        const { data } = await axios.get(`/auth/user-validation`, {
          params: { email: formValues.email, PlatformId: platformData.id }
        });
        setUser(data);
        await sendOTPAsync({ UserId: data.id });
        dispatch(
          flashDetailedToastNotification({ details: "One-Time Password sent to your email" })
        );
        return setIsOTPSent(true);
      } catch (err) {
        if (err.response?.status === 404) {
          dispatch(flashDetailedToastNotification({ details: "User not found", type: "error" }));
        } else {
          dispatch(
            flashDetailedToastNotification({ details: "Something went wrong", type: "error" })
          );
          console.error("Failed user validation", err);
        }
        return;
      }
    }

    if (isOTPLoginChosen && isOTPSent) {
      try {
        const action = await dispatch(signinWithOTPAsync({ UserId: user.id, otp: formValues.otp }));
        if (action.type.includes("fulfilled")) dismissAuthPrompt();
      } catch (err) {
        console.error("Failed to sign in with OTP", err);
        // no need to show toast notification here, it's handled in the authSlice
      }
    }

    if (isPasswordUpdate) {
      try {
        const action = await dispatch(
          setPasswordAsync({ userId: user.id, password: formValues.password })
        );
        if ("auth/set-password/fulfilled" === action.type) {
          dismissAuthPrompt();
          dispatch(clearUserErrors());
          const userToUpdate = { id: user.id, status: "active", providerId: null };
          if (user?.id && !user.PlatformId) userToUpdate.PlatformId = platformData.id;
          await dispatch(upsertUserAsync({ user: userToUpdate }));
          await dispatch(loginAsync({ email: formValues.email, password: formValues.password }));
        }
      } catch (err) {
        console.error("Failed to set password", err);
      }
    }

    if (isPasswordLogin) {
      try {
        // validate user by email
        // the main purpose of this check is to see if the user has a password set
        // but it also has the effect of checking if the user exists in the db
        const { data } = await axios.get(`/auth/user-validation`, {
          params: { email: formValues.email, PlatformId: platformData.id }
        });
        // if the user exists in the db but doesn't have a password set
        // we need to prompt them to update their password before logging in
        setUser(data);
        if (data && !data.isPasswordSet) return setAuthFlowChosen("setPassword");

        // if the user exists in the db and has a password set, proceed with login
        const action = await dispatch(loginAsync({ ...formValues, PlatformId: platformData.id }));
        if ("auth/signin/fulfilled" === action.type) {
          setFormValues({}); // clear form values
          dismissAuthPrompt();
          return;
        }
      } catch (err) {
        if (err.response?.status === 404) {
          dispatch(flashDetailedToastNotification({ details: "User not found", type: "error" }));
        } else {
          dispatch(
            flashDetailedToastNotification({ details: "Something went wrong", type: "error" })
          );
          console.error("Failed user validation", err);
        }
        return;
      }
    }

    if (isAccountCreation) {
      const user = { ...formValues, role: "free" };
      if (user.password.length < 8)
        return setErrorPassword("Passwords must be at least 8 characters");

      const action = await dispatch(upsertUserAsync({ user, PlatformId: platformData.id }));
      if ("user/upsert/fulfilled" === action.type) {
        dismissAuthPrompt();
        dispatch(clearUserErrors());
        await dispatch(
          loginAsync({
            email: formValues.email,
            password: formValues.password,
            PlatformId: platformData.id
          })
        );
      }
    }
  };

  const handleBack = () => {
    setAuthFlowChosen("initial");
    setIsOTPSent(false);
    setUser(null);
  };

  return (
    <form onSubmit={event => onSubmit(event)}>
      {/* Back button - show for non-initial screens */}
      {!isInitialScreen && !isPasswordUpdate && (
        <button
          type="button"
          onClick={handleBack}
          className="absolute top-4 left-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition"
          aria-label="Go back"
        >
          <BackIcon />
        </button>
      )}

      <h2 className="text-center text-3xl leading-9 font-bold">{titleText}</h2>
      {subText && (
        <p className="text-gray-800 text-lg text-center mt-4" data-testid="auth-form-subtext">
          {subText}
        </p>
      )}

      {loading ? (
        <div className="flex justify-center mt-6">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {/* Google auth button - always show on initial screen */}
          {isInitialScreen && isGoogleAuthEnabled && (
            <div className="mt-6">
              <a
                href={googleHref}
                className="w-full flex items-center justify-center py-3 px-4 border border-gray-200 text-sm font-medium rounded-md text-gray-800 bg-gray-50 hover:bg-gray-100 transition ease-in-out duration-150 shadow-sm"
                data-testid="google-auth-link"
              >
                <GoogleIcon />
                Continue with Google
              </a>
              {/* Divider */}
              <div className="relative mt-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>
            </div>
          )}
          {/* Error messages - centered for OTP screen, left-aligned for others */}
          {error && (
            <p className={`text-sm leading-5 font-medium text-red-800 mt-4 ${isOTPLoginChosen && isOTPSent ? 'text-center' : ''}`}>
              {error}
            </p>
          )}
          {errorPassword && (
            <p className="text-sm leading-5 font-medium text-red-800 mt-4">{errorPassword}</p>
          )}

          {/* Show name field first when creating account */}
          {isAccountCreation && (
            <InputField
              name="name"
              label="Full name"
              value={formValues.name || ""}
              onChange={onChange}
              required
              autoFocus
              tabIndex={1}
              placeholder="Michael Scott"
            />
          )}

          {/* Email input - always show on initial screen and other flows */}
          {(isInitialScreen ||
            (isAccountCreation && !isInitialScreen) ||
            isPasswordLogin ||
            (isOTPLoginChosen && !isOTPSent)) && (
            <InputField
              name="email"
              label="Email address"
              value={formValues?.email || ""}
              onChange={onChange}
              required
              autoFocus={true}
              tabIndex={isAccountCreation ? 2 : 1}
              placeholder="Enter email address"
            />
          )}

          {/* Side-by-side auth buttons - always show on initial screen */}
          {isInitialScreen && (
            <div className="mt-4 flex items-center justify-between gap-3">
              <button
                className="py-3 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-800 bg-white hover:bg-gray-50 transition ease-in-out duration-150 w-[48%] shadow-sm"
                onClick={() => {
                  if (!formValues?.email) {
                    document.querySelector("[name=email]")?.focus();
                    return;
                  }
                  onChoosePasswordLogin();
                }}
                type="button"
                tabIndex={2}
              >
                Enter Password
              </button>
              <button
                type="submit"
                className="py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-platform-primary hover:opacity-90 transition ease-in-out duration-150 w-[48%] shadow-sm"
                tabIndex={3}
              >
                Send One-Time Code
              </button>
            </div>
          )}

          {(isAccountCreation || isPasswordUpdate || isPasswordLogin) && (
            <InputField
              name="password"
              label={isPasswordUpdate ? "New password" : "Password"}
              value={formValues.password || ""}
              onChange={onChange}
              type="password"
              required
              tabIndex={isAccountCreation ? 3 : 2}
            />
          )}

          {isPasswordLogin && !isPasswordUpdate && <ResetPasswordButton />}

          {isPasswordUpdate && (
            <InputField
              name="confirmPassword"
              label="Confirm password"
              value={formValues.confirmPassword || ""}
              onChange={onChange}
              type="password"
              required
              tabIndex={4}
            />
          )}

          {isOTPLoginChosen && isOTPSent && (
            <OTPInput
              value={formValues.otp || ""}
              onChange={onChange}
              autoFocus={true}
            />
          )}

          {/* Main action button - show for non-initial flows */}
          {!isInitialScreen && (
            <button
              type="submit"
              className="mt-6 w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-platform-primary focus:outline-none transition duration-150 ease-in-out shadow-sm"
              tabIndex={isOTPLoginChosen && isOTPSent ? 7 : 5}
            >
              {isOTPLoginChosen && isOTPSent
                ? "Verify Code"
                : isAccountCreation
                ? "Create my Account"
                : isPasswordUpdate
                ? "Update Password"
                : isPasswordLogin
                ? "Sign In"
                : "Continue"}
            </button>
          )}

          {/* Terms and Conditions for account creation */}
          {isAccountCreation && platformData?.userTermsConditions && (
            <p className="text-gray-500 text-sm mt-2 text-center">
              By creating an account, you agree to the{" "}
              <a
                href={platformData.userTermsConditions}
                className="text-blue-500"
                style={{ color: platformData?.primaryColor }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms and Conditions
              </a>
            </p>
          )}

          {/* OTP-specific options - show after Verify button */}
          {isOTPLoginChosen && isOTPSent && (
            <div className="mt-2 flex items-center justify-center text-sm leading-5">
              <button
                onClick={() => {
                  setFormValues({}); // clear form values
                  setIsOTPSent(false);
                  // document.querySelector("[name=email]").focus();
                }}
                type="button"
                className="font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline transition ease-in-out duration-150"
                style={{ color: platformData?.primaryColor }}
                tabIndex={8}
              >
                Try another email
              </button>
            </div>
          )}

          {/* Alternative login method for password screen */}
          {isPasswordLogin && !isPasswordUpdate && (
            <div className="mt-4 flex items-center justify-center">
              <button
                type="button"
                onClick={() => {
                  setAuthFlowChosen("otp");
                  setIsOTPSent(false);
                }}
                className="text-sm text-gray-700 hover:text-gray-900 underline font-medium"
              >
                Use One-Time Code instead
              </button>
            </div>
          )}
        </>
      )}
    </form>
  );
};

export default AuthForm;
